import React, { useCallback, useEffect, useRef } from 'react';
import { JitsiMeeting } from '@jitsi/react-sdk';
import { useDispatch, useSelector } from 'react-redux';

import { showAlert, showMessage } from '../../../actions/globalActions';
import { messageTypes } from '../../../constants/mediaConstants';
import {
	setHasActiveCall,
	setJitsiStreamActuallyStarted,
	startJibriStreaming,
	stopJibriStreaming,
	setJitsiApi,
	setJitsiRoomName,
} from '../../../actions/liveManagerActions';
import { LocalStorageService } from '../../../services/localStorageService';
import { meetQbrickDomain, meetQbrickWebSocketUrl, readApiEndpointV1 } from '../../../utils/config';
import { getRoomName } from '../../../utils/meetingUtils';

import './LiveMeetingContainer.scss';

interface LiveMeetingContainerProps {
	mediaDetails?: any;
	liveChannel?: any;
	channels?: any[];
	children?: React.ReactNode;
	meetingContainerRef?: React.RefObject<HTMLDivElement>;
	isStreamPublished?: boolean;
}

const ToolbarButtons = [
	'microphone',
	'camera',
	'desktop',
	'chat',
	'participants-pane',
	'invite',
	'livestreaming',
	'hangup',
	'settings',
	'fullscreen',
];

const LiveMeetingContainer = ({
	mediaDetails,
	liveChannel,
	channels,
	children,
	meetingContainerRef,
}: LiveMeetingContainerProps) => {
	const dispatch = useDispatch();

	const { hasActiveCall, isJibriStreaming, meetJoinToken } = useSelector((state: any) => state.liveManager);
	const { username, defaultAccountId } = useSelector((state: any) => state.session);
	const externalApiRef = useRef<any>(null);

	const [renewMeetingTrigger, setRenewMeetingTrigger] = React.useState(Date.now());
	const [jwtToken, setJwtToken] = React.useState<string | null>(null); // <-- Add this
	const [roomName, setRoomName] = React.useState<string>('');

	useEffect(() => {
		setRoomName(getRoomName(mediaDetails));
	}, [mediaDetails]);

	const handleBeforeUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			if (hasActiveCall) {
				const mediaId = mediaDetails?.id;
				if (mediaId) {
					LocalStorageService.setLiveMeetingState(mediaId, 'false');
				}

				event.preventDefault();
				const confirmationMessage = 'prompt-before-unload';
				(event || window.event).returnValue = confirmationMessage;
				return confirmationMessage;
			}
		},
		[hasActiveCall, mediaDetails?.id]
	);

	useEffect(() => {
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [handleBeforeUnload]);

	useEffect(() => {
		if (externalApiRef.current) {
			if (isJibriStreaming) {
				const selectedChannel = channels?.find((ch) => ch.id === liveChannel?.id);

				if (!selectedChannel) {
					showMessage('No channel selected for streaming!', messageTypes.error);
					return;
				}

				const streamUrl = selectedChannel.ingestPoint?.ingestURL;
				const streamKey = selectedChannel.ingestPoint?.authentication?.password;
				const streamId = selectedChannel.id;

				if (!streamUrl || !streamKey || !streamId) {
					showMessage(`Missing stream URL or key for channel: ${selectedChannel?.id}`, messageTypes.error);
					return;
				}

				const cleanStreamUrl = streamUrl.endsWith('/') ? streamUrl.slice(0, -1) : streamUrl;
				const fullStreamUrl = `${cleanStreamUrl}${streamKey}/${streamId}`;

				externalApiRef.current.executeCommand('startRecording', {
					mode: 'stream',
					streamId: `stream-${roomName}`,
					rtmpStreamKey: fullStreamUrl,
				});
			} else {
				externalApiRef.current.executeCommand('stopRecording', 'stream');
			}
		}
	}, [isJibriStreaming, roomName, liveChannel, channels, dispatch]);

	useEffect(() => {
		if (externalApiRef.current) {
			const toolbarButtons = ToolbarButtons;
			if (!isJibriStreaming) {
				toolbarButtons.push('hangup');
			}
			externalApiRef.current.executeCommand('overwriteConfig', {
				toolbarButtons: toolbarButtons,
			});
		}
	}, [isJibriStreaming]);

	// Fetch JWT token from operator-token endpoint
	useEffect(() => {
		const fetchToken = async () => {
			const response = await fetch(
				`${readApiEndpointV1}accounts/${defaultAccountId}/meetings/${roomName}/operator-token`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${LocalStorageService.getAccessToken()}`,
					},
					body: JSON.stringify({
						userId: Math.floor(10000000000 + Math.random() * 90000000000).toString(),
						meetId: roomName,
						confId: '*',
						name: username.split('@')?.[0] || username,
						email: username,
						avatar: '',
					}),
				}
			);

			const data = await response.json();
			setJwtToken(data);
		};

		fetchToken();
	}, [roomName, defaultAccountId, username]);

	if (!jwtToken) {
		return null;
	}

	return (
		<div className="live-meeting-container" ref={meetingContainerRef}>
			{children}
			<JitsiMeeting
				/* The token is part of the key so anything that changes what the meeting would resolve
				   on join - attaching the form that links this media to the room - can make it join
				   again, instead of running on the answer it got the first time. */
				key={`${renewMeetingTrigger}-${meetJoinToken}`}
				domain={meetQbrickDomain}
				roomName={roomName}
				getIFrameRef={(iframeRef) => {
					iframeRef.style.height = '100%';
				}}
				jwt={jwtToken} // <-- Pass JWT token here
				configOverwrite={{
					disableInviteFunctions: true,
					enableClosePage: false,
					bosh: undefined,
					preferBosh: false,
					websocket: meetQbrickWebSocketUrl,
					startWithAudioMuted: true,
					startWithVideoMuted: true,
					prejoinConfig: { enabled: true },
					liveStreamingEnabled: true,
					fileRecordingsEnabled: true,
					jibri: {
						enableStreaming: true,
					},
					resolution: 1080,
					constraints: {
						video: { height: { ideal: 1080, max: 1080 }, frameRate: { ideal: 25, max: 25 } },
					},
					desktopSharingFrameRate: { min: 25, max: 25 },
					audioQuality: {
						stereo: false,
						opusMaxAverageBitrate: 96000,
					},
					p2p: { enabled: false },
					webrtcIceTcpDisable: false,
					webrtcIceUdpDisable: false,
					disableSimulcast: false,
					enableTcc: true,
					enableRemb: true,
					toolbarButtons: ToolbarButtons,
					liveStreaming: {
						helpLink: 'https://support.qbrick.com/hc/en-us',
					},
				}}
				interfaceConfigOverwrite={{
					SHOW_JITSI_WATERMARK: false,
					SHOW_WATERMARK_FOR_GUESTS: false,
					LIVE_STREAMING_ENABLED: true,
					SUPPORT_URL: 'https://support.qbrick.com/hc/en-us',
					LIVE_STREAMING_ERROR_CONTACT_SUPPORT_URL: 'https://support.qbrick.com/hc/en-us',
				}}
				onApiReady={(externalApi) => {
					externalApiRef.current = externalApi;
					dispatch(setJitsiApi(externalApi));
					dispatch(setJitsiRoomName(roomName));

					externalApi.on('participantRoleChanged', (event) => {
						if (event.role === 'moderator') {
							externalApi.executeCommand('setFollowMe', true);
						}
					});

					externalApi.on('videoConferenceJoined', () => {
						const mediaId = mediaDetails?.id;
						if (mediaId) {
							LocalStorageService.setLiveMeetingState(mediaId, 'true');
						}
						dispatch(setHasActiveCall(true));
						dispatch(
							showAlert(
								'You are connected to the meeting. Do not close this window until you leave the meeting.',
								messageTypes.warning
							)
						);
					});

					externalApi.on('readyToClose', () => {
						dispatch(setHasActiveCall(false));
						setTimeout(() => {
							setRenewMeetingTrigger(Date.now());
						}, 100);
					});

					externalApi.on('videoConferenceLeft', () => {
						dispatch(setHasActiveCall(false));
					});

					externalApi.on('recordingStatusChanged', (data: any) => {
						if (data.on && data.mode === 'stream') {
							dispatch(startJibriStreaming());
							dispatch(setJitsiStreamActuallyStarted());
						} else if (data.status === 'off') {
							dispatch(stopJibriStreaming());
						}
					});
					externalApi.on('errorOccurred', (data: any) => {
						console.error('Jitsi error:', JSON.stringify(data));
					});
				}}
			/>
		</div>
	);
};

export default LiveMeetingContainer;
